import React, { useEffect, useMemo, useState } from "react";

export default function PatientRadioPicker({
  initialChannels = [],
  max = 5,
  onSaved,
}) {
  const [editMode, setEditMode] = useState(false);
  const selected = initialChannels;
  const [draft, setDraft] = useState(initialChannels);
  const [err, setErr] = useState("");

  const [region, setRegion] = useState("");
  const [query, setQuery] = useState("");
  const [regionStations, setRegionStations] = useState({});

  // Load stations
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/radio/stations`)
      .then((r) => {
        if (!r.ok) throw new Error("Radiosender konnten nicht geladen werden.");
        return r.json();
      })
      .then((data) => {
        setRegionStations(data.stations);
        const firstRegion = Object.keys(data.stations)[0];
        if (firstRegion) setRegion(firstRegion);
      })
      .catch((e) => setErr(e.message));
  }, []);

  const byId = useMemo(
    () => new Map(Object.values(regionStations).flat().map((station) => [station.id, station])),
    [regionStations],
  );

  const current = editMode ? draft : selected;
  const selectedIds = new Set(current);
  const regionList = (regionStations[region] || []).filter((station) => !selectedIds.has(station.id));
  const needle = query.trim().toLowerCase();
  const matchRank = (station) => {
    const name = station.name.toLowerCase();
    return name === needle ? 0 : name.startsWith(needle) ? 1 : 2;
  };
  const searchList = Array.from(byId.values())
    .filter((station) => !selectedIds.has(station.id) && station.name.toLowerCase().includes(needle))
    .sort((a, b) => matchRank(a) - matchRank(b) || a.popularity_rank - b.popularity_rank)
    .slice(0, 30).map((station) => station.id);
  const nameFor = (id) => byId.get(id)?.name || id;
  const defaultIcon = `${process.env.REACT_APP_API_URL}/static/icons/radio.png`;
  const iconFor = (id) => byId.get(id)?.icon || defaultIcon;
  const imageError = (event) => {
    if (event.currentTarget.getAttribute("src") !== defaultIcon) event.currentTarget.src = defaultIcon;
  };

  function add(id) {
    setErr("");
    if (draft.length >= max) {
      setErr(`Maximal ${max} Sender auswählbar.`);
      return;
    }
    if (!selectedIds.has(id)) setDraft((prev) => [...prev, id]);
  }

  function remove(id) {
    setErr("");
    if (editMode) setDraft((prev) => prev.filter((item) => item !== id));
    else onSaved(selected.filter((item) => item !== id));
  }

  function openOverlay() {
    setEditMode(true);
    setErr("");
    setDraft([...selected]); // start from committed state
  }

  function handleSave() {
    onSaved(draft);
    closeOverlay();
  }

  function closeOverlay() {
    setEditMode(false);
    setQuery("");
  }

  function onKeyDownOverlay(e) {
    if (e.key === "Escape") closeOverlay();
  }

  return (
    <div className="rounded-lg border border-black/10 bg-blue-50 p-3">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Radio</h3>
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border text-[11px] text-gray-600">i</span>
          <span className="text-sm text-gray-600">Wähle bis zu {max} Kanäle aus</span>
        </div>
        {!editMode ? (
          <button onClick={openOverlay} className="rounded-full border border-gray-300 p-1.5 hover:bg-white/70" title="Bearbeiten">
            <svg viewBox="0 0 24 24" className="h-4 w-4">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/>
              <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/>
            </svg>
          </button>
        ) : (
          <button onClick={closeOverlay} className="rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-white/70">
            Schließen
          </button>
        )}
      </div>

      {err && (
        <div className="mb-2 rounded-md bg-red-50 border border-red-200 px-3 py-1.5 text-xs text-red-800">
          {err}
        </div>
      )}

      {/* Read view: committed selection */}
      <ul className="space-y-2">
        {selected.map((name) => (
          <li key={name} className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <img loading="lazy" src={iconFor(name)} onError={imageError} alt="" className="h-8 w-8 shrink-0 rounded border border-black/10 bg-white object-contain" draggable="false" />
              <span className="line-clamp-2 break-words text-gray-900 font-medium" title={nameFor(name)}>{nameFor(name)}</span>
            </div>
            {!editMode && (
              <button onClick={() => remove(name)} className="shrink-0 text-sm text-gray-600 hover:underline">
                Entfernen
              </button>
            )}
          </li>
        ))}
        {selected.length === 0 && (
          <li className="text-sm text-gray-600">
            Keine Sender ausgewählt.{" "}
            <button onClick={openOverlay} className="underline underline-offset-2">
              Bearbeiten
            </button>
          </li>
        )}
      </ul>

      {/* Overlay */}
      {editMode && (
        <div className="fixed inset-0 z-40" aria-modal="true" role="dialog" onKeyDown={onKeyDownOverlay}>
          <div className="absolute inset-0 bg-black/30" onClick={closeOverlay} />
          <div className="absolute left-1/2 top-6 z-50 w-[min(1100px,92vw)] -translate-x-1/2 rounded-xl border border-black/10 bg-[#e9f4ff] shadow-2xl">
            {/* header */}
            <div className="flex items-center justify-between border-b border-black/10 px-4 py-3">
              <div className="text-lg font-semibold text-[#0b1b2d]">Radio auswählen</div>
              <div className="flex gap-2">
                <button onClick={handleSave} className="rounded-md bg-[#76B3FF] px-3 py-1.5 text-white text-sm hover:bg-[#5d9ef7]">
                  Speichern
                </button>
                <button onClick={closeOverlay} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/70">
                  Abbrechen
                </button>
              </div>
            </div>

            {/* body */}
            <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-[1.1fr_1.1fr_1fr]">
              {/* Left: Bundesland list (filtered against current) */}
              <div className="rounded-lg bg-white/70 p-3">
                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Bundesland</label>
                  <div className="relative">
                    <select
                      className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-blue-400"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                    >
                      {Object.keys(regionStations).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">▾</span>
                  </div>
                </div>

                <div className="max-h-[260px] overflow-auto rounded-md border border-black/10">
                  {regionList.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-600">Keine weiteren Sender in {region}.</div>
                  ) : (
                    regionList.map(({ id: n }) => (
                      <button
                        key={n}
                        onClick={() => add(n)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                      >
                        <img loading="lazy" src={iconFor(n)} onError={imageError} alt="" className="h-6 w-6 shrink-0 rounded border border-black/10 bg-white object-contain" draggable="false" />
                        <span className="min-w-0 line-clamp-2 break-words" title={nameFor(n)}>{nameFor(n)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Middle: Search */}
              <div className="rounded-lg bg-white/70 p-3">
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="radio-search">
                  suchen
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">🔎</span>
                  <input
                    autoFocus
                    id="radio-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Sender suchen…"
                    className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div className="mt-2 max-h-[260px] overflow-auto rounded-md border border-black/10">
                  {!query ? (
                    <div className="px-3 py-2 text-sm text-gray-600">
                      Tippe, um in allen Bundesländern zu suchen.
                    </div>
                  ) : searchList.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-600">
                      Keine Treffer für <b>{query}</b>.
                    </div>
                  ) : (
                    searchList.map((n) => (
                      <button
                        key={n}
                        onClick={() => add(n)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                      >
                        <img loading="lazy" src={iconFor(n)} onError={imageError} alt="" className="h-6 w-6 shrink-0 rounded border border-black/10 bg-white object-contain" draggable="false" />
                        <span className="min-w-0 line-clamp-2 break-words" title={nameFor(n)}>{nameFor(n)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Right: Draft preview */}
              <div className="rounded-lg bg-white/70 p-3">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  Auswahl ({draft.length}/{max})
                </div>
                <div className="max-h-[306px] overflow-auto rounded-md border border-black/10">
                  {draft.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-600">Noch keine Auswahl.</div>
                  ) : (
                    draft.map((n) => (
                      <div key={n} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-blue-50">
                        <div className="flex min-w-0 items-center gap-2">
                          <img loading="lazy" src={iconFor(n)} onError={imageError} alt="" className="h-6 w-6 shrink-0 rounded border border-black/10 bg-white object-contain" draggable="false" />
                          <span className="line-clamp-2 break-words font-medium" title={nameFor(n)}>{nameFor(n)}</span>
                        </div>
                        <button onClick={() => remove(n)} className="shrink-0 text-gray-600 hover:text-gray-900" title="Entfernen">
                          🗑️
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* footer (error area only) */}
            <div className="flex items-center justify-end gap-2 border-t border-black/10 px-4 py-3">
              {err && <div className="mr-auto rounded bg-red-50 px-2 py-1 text-xs text-red-700">{err}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
